import React, { useEffect, useState } from "react";
import { collection, query, getDocs, orderBy, where, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import Layout from "../components/Layout";
import { generateOutletInvoicePdf } from "../utils/pdfOutletInvoice";

function OutletInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [company, setCompany] = useState(null);
  
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [searchTerm, setSearchTerm] = useState("");
  
  const [parties, setParties] = useState([]);
  const [selectedParty, setSelectedParty] = useState("");

  // Target exactly the orders for that day to find ONLY billed parties (0 overhead)
  useEffect(() => {
    const fetchActiveParties = async () => {
      if (!selectedDate) return;
      try {
        const q = query(
          collection(db, "orders"),
          where("approvedDateOnly", "==", selectedDate),
          where("status", "==", "APPROVED")
        );
        const snap = await getDocs(q);
        
        const uniqueParties = {};
        snap.docs.forEach(d => {
          const data = d.data();
          if (data.partyId && data.partyName) {
            uniqueParties[data.partyId] = data.partyName;
          }
        });

        const partyArray = Object.entries(uniqueParties).map(([id, name]) => ({
          partyId: id,
          name: name
        }));
        
        partyArray.sort((a, b) => a.name.localeCompare(b.name));
        setParties(partyArray);
        setSelectedParty("");
      } catch (err) {
        console.error("Error fetching active parties:", err);
      }
    };
    fetchActiveParties();
  }, [selectedDate]);

  useEffect(() => {
    // 1. Fetch Company Info on mount
    const fetchCompany = async () => {
      const cSnap = await getDoc(doc(db, "companyInfo", "default"));
      if (cSnap.exists()) setCompany(cSnap.data());
    };
    fetchCompany();
  }, []); // Blank dependency array so it doesn't query invoices

  const fetchInvoices = async () => {
    if (!selectedDate) {
      alert("Please select a date first.");
      return;
    }
    if (!selectedParty) {
      alert("Please select a Party from the dropdown (or select 'ALL Parties' at the bottom).");
      return;
    }
    
    setLoading(true);
    setInvoices([]); // clear prev
    
    try {
      let q;
      // 2. EXPLICITLY filter by approvedDateOnly + dynamically targeted Party
      if (selectedParty === "ALL") {
        q = query(
          collection(db, "outletOrders"), 
          where("approvedDateOnly", "==", selectedDate)
        );
      } else {
        q = query(
          collection(db, "outletOrders"), 
          where("approvedDateOnly", "==", selectedDate),
          where("partyId", "==", selectedParty)
        );
      }
      const snap = await getDocs(q);
      
      const grouped = {};
      snap.forEach(d => {
        const data = d.data();
        const id = data.displayOrderId;
        if (!id) return;

        if (!grouped[id]) {
          grouped[id] = {
            invoiceNo: id,
            date: data.approvedDateOnly ? data.approvedDateOnly.split("-").reverse().join("-") : "",
            partyName: data.partyName || "",
            outletName: data.outletName || "",
            address: data.address || "",
            partyPhone: data.partyPhone || "",
            gstno: data.gstno || "",
            outletAddress: data.outletAddress || "",
            outletPhone: data.outletPhone || "",
            outletGstin: data.outletGstin || "",
            outletStateCode: data.outletStateCode || "",
            totalQty: 0,
            totalBasic: 0,
            totalCgst: 0,
            totalSgst: 0,
            grandTotal: 0,
            items: []
          };
        }

        if (data.lines && Array.isArray(data.lines)) {
          data.lines.forEach(line => {
            const basic = Number(line.basicAmount || 0);
            const gst = Number(line.gstAmount || 0);
            const total = Number(line.totalAmount || line.lineTotal || line.ewayLineTotal || 0);
            const cgst = Number(line.cgstAmount || (gst / 2) || 0);
            const sgst = Number(line.sgstAmount || (gst / 2) || 0);

            grouped[id].totalQty += Number(line.qty || 0);
            grouped[id].totalBasic += basic;
            grouped[id].totalCgst += cgst;
            grouped[id].totalSgst += sgst;
            grouped[id].grandTotal += total;

            grouped[id].items.push({
              skuName: line.skuName || line.description,
              hsn: line.hsn || "",
              rate: Number(line.rate || line.basicRate || 0),
              mrp: Number(line.mrp || 0),
              qty: Number(line.qty || 0),
              gstPercent: Number(line.gstPercent || 0),
              basicAmount: basic,
              gstAmount: gst,
              totalAmount: total
            });
          });
        } else {
          const basic = Number(data.basicAmount || 0);
          const gst = Number(data.gstAmount || 0);
          const total = Number(data.totalAmount || 0);
          const cgst = Number(data.cgstAmount || 0);
          const sgst = Number(data.sgstAmount || 0);

          grouped[id].totalQty += Number(data.qty || 0);
          grouped[id].totalBasic += basic;
          grouped[id].totalCgst += cgst;
          grouped[id].totalSgst += sgst;
          grouped[id].grandTotal += total;
          
          grouped[id].items.push({
            skuName: data.skuName,
            hsn: data.hsn,
            rate: Number(data.rate || 0),
            mrp: Number(data.mrp || 0),
            qty: Number(data.qty || 0),
            gstPercent: Number(data.gstPercent || 0),
            basicAmount: basic,
            gstAmount: gst,
            totalAmount: total
          });
        }
      });

      setInvoices(Object.values(grouped));
    } catch (err) {
      console.error("Error fetching invoices:", err);
      alert("Failed to fetch invoices.");
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = (inv) => {
    const pdfDoc = generateOutletInvoicePdf(inv.invoiceNo, inv, company);
    pdfDoc.save(`Invoice-${inv.invoiceNo}.pdf`);
  };

  // 3. Quick client-side filtering system
  const filteredInvoices = invoices.filter(inv => {
    const s = searchTerm.toLowerCase();
    return inv.invoiceNo.toLowerCase().includes(s) ||
           inv.partyName.toLowerCase().includes(s) ||
           inv.outletName.toLowerCase().includes(s);
  });

  return (
    <Layout>
      <div style={{ padding: "20px" }}>
        <h2 style={{ marginBottom: "20px" }}>Billed Outlet Invoices</h2>
        
        {/* --- NEW SECURITY & FILTER CONTROL BAR --- */}
        <div style={{ display: "flex", gap: "15px", marginBottom: "30px", background: "#f8f9fa", padding: "20px", borderRadius: "8px", flexWrap: "wrap", alignItems: "center" }}>
          <div>
            <label style={{ display: "block", fontSize: "12px", color: "#666", marginBottom: "4px", fontWeight: "bold" }}>TARGET INVOICE DATE</label>
            <input 
              type="date" 
              value={selectedDate} 
              onChange={(e) => setSelectedDate(e.target.value)} 
              style={{ padding: "10px", borderRadius: "5px", border: "1px solid #ccc", minWidth: "150px" }}
            />
          </div>

          <div style={{ minWidth: "250px" }}>
            <label style={{ display: "block", fontSize: "12px", color: "#666", marginBottom: "4px", fontWeight: "bold" }}>SELECT PARTY ({parties.length} ACTIVE TODAY)</label>
            <select 
              value={selectedParty} 
              onChange={e => setSelectedParty(e.target.value)}
              style={{ padding: "10px", borderRadius: "5px", border: "1px solid #ccc", width: "100%", background: "#fff" }}
              disabled={parties.length === 0}
            >
              <option value="" disabled>
                {parties.length === 0 ? `-- No Parties Billed on ${selectedDate} --` : `-- Select an Active Party --`}
              </option>
              {parties.map(p => (
                <option key={p.partyId} value={p.partyId}>
                  {p.partyId === p.name ? p.name : `${p.partyId} - ${p.name}`}
                </option>
              ))}
              {parties.length > 0 && <option disabled>───────────</option>}
              {parties.length > 0 && <option value="ALL" style={{ fontWeight: "bold", color: "#d32f2f" }}>⚠️ Select ALL {parties.length} Parties (High Cost Warning)</option>}
            </select>
          </div>

          <div style={{ marginTop: "18px" }}>
            <button 
              onClick={fetchInvoices} 
              disabled={loading}
              style={{ background: "#20c997", color: "#fff", border: "none", padding: "10px 24px", borderRadius: "5px", cursor: "pointer", fontWeight: "bold" }}
            >
              {loading ? "Fetching..." : "📥 Fetch Invoices"}
            </button>
          </div>

          <div style={{ marginLeft: "auto", marginTop: "18px" }}>
             <input 
               type="text" 
               placeholder="🔍 Filter by Party, Outlet, or No" 
               value={searchTerm} 
               onChange={(e) => setSearchTerm(e.target.value)} 
               style={{ padding: "10px", borderRadius: "5px", border: "1px solid #ccc", minWidth: "280px" }}
             />
          </div>
        </div>

        {invoices.length === 0 && !loading ? (
           <div style={{ padding: 40, textAlign: "center", color: "#888", border: "1px dashed #ccc", borderRadius: 8 }}>
             No data fetched. Please select a date and click "Fetch Invoices" to begin.
           </div>
        ) : (
          <table border="1" cellPadding="10" style={{ width: "100%", borderCollapse: "collapse", background: "#fff" }}>
            <thead>
              <tr style={{ background: "#f8f9fa" }}>
                <th>Date</th>
                <th>Invoice No</th>
                <th>Party / Outlet</th>
                <th>Qty</th>
                <th>Basic</th>
                <th>CGST</th>
                <th>SGST</th>
                <th>Total</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.map(inv => (
                <tr key={inv.invoiceNo}>
                  <td>{inv.date}</td>
                  <td><strong>{inv.invoiceNo}</strong></td>
                  <td>
                    <div>{inv.partyName}</div>
                    <div style={{ fontSize: "11px", color: "#666" }}>{inv.outletName}</div>
                  </td>
                  <td align="right">{inv.totalQty.toFixed(2)}</td>
                  <td align="right">₹{inv.totalBasic.toFixed(2)}</td>
                  <td align="right">₹{inv.totalCgst.toFixed(2)}</td>
                  <td align="right">₹{inv.totalSgst.toFixed(2)}</td>
                  <td align="right"><strong>₹{inv.grandTotal.toFixed(2)}</strong></td>
                  <td align="center">
                    <button 
                      onClick={() => downloadPdf(inv)}
                      style={{ padding: "6px 12px", background: "#0d6efd", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer" }}
                    >
                      Download PDF
                    </button>
                  </td>
                </tr>
              ))}
              
              {filteredInvoices.length === 0 && invoices.length > 0 && (
                <tr>
                   <td colSpan="9" style={{ textAlign: "center", padding: "20px" }}>No invoices match your search term.</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </Layout>
  );
}

export default OutletInvoices;
